import type { CrmCustomerDocumentType } from '../api/schemas';
import { z } from 'zod';
import { browserCalendarTimeZone, calendarDateInTimeZone } from '@shared/lib';

export const customerDocumentCommandTypes = [
  'medical-questionnaire',
  'paper-completion-form',
  'paper-exam',
  'certification-card',
  'student-liability-release',
  'instructor-insurance-waiver',
  'first-aid-certificate',
] as const satisfies readonly CrmCustomerDocumentType[];

export type CustomerDocumentCommandType = (typeof customerDocumentCommandTypes)[number];

/** Bubble Create excludes In review for signature-required Medical/Liability types. */
export function customerDocumentCreateStatuses(type: CustomerDocumentCommandType) {
  return type === 'medical-questionnaire' || type === 'student-liability-release'
    ? (['draft', 'approved', 'not-valid'] as const)
    : (['draft', 'in-review', 'approved', 'not-valid'] as const);
}

export function customerDocumentDefaultStatus(type: CustomerDocumentCommandType) {
  return type === 'medical-questionnaire' || type === 'student-liability-release'
    ? 'draft'
    : 'in-review';
}

export const customerDocumentMaxFileBytes = 20 * 1024 * 1024;
export const customerDocumentFileAccept = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp';

const customerDocumentMimeTypeByExtension = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  webp: 'image/webp',
} as const;

export type CustomerDocumentFileMimeType =
  (typeof customerDocumentMimeTypeByExtension)[keyof typeof customerDocumentMimeTypeByExtension];

const allowedFileTypes = new Set<CustomerDocumentFileMimeType>(
  Object.values(customerDocumentMimeTypeByExtension)
);

export type CustomerDocumentFormRules = {
  certificationType: 'hidden' | 'required';
  certifyingAgency: 'hidden' | 'optional' | 'required';
  expiration: 'hidden' | 'optional' | 'required';
  notes: 'hidden' | 'optional';
  paperExam: boolean;
  registration: 'hidden' | 'optional' | 'required';
};

export type CustomerDocumentFormValues = {
  certifyingAgency: string;
  certificationTypeId: string;
  examResultId: string;
  expiresAt: string;
  educationModuleId: string;
  paperExamDate: string;
  registrationId: string;
  submittingInstructorId: string;
};

const hasValue = (value: string) => value.trim().length > 0;

export function getCustomerDocumentFormRules(
  type: CustomerDocumentCommandType
): CustomerDocumentFormRules {
  const registration = new Set<CustomerDocumentCommandType>([
    'medical-questionnaire',
    'paper-completion-form',
    'paper-exam',
    'certification-card',
    'student-liability-release',
  ]).has(type);
  const notes = new Set<CustomerDocumentCommandType>([
    'paper-completion-form',
    'paper-exam',
    'certification-card',
  ]).has(type);
  const expiration =
    type === 'paper-completion-form'
      ? 'optional'
      : type === 'instructor-insurance-waiver' || type === 'first-aid-certificate'
        ? 'required'
        : 'hidden';
  return {
    certificationType:
      type === 'paper-completion-form' || type === 'certification-card' ? 'required' : 'hidden',
    certifyingAgency: type === 'certification-card' ? 'required' : 'hidden',
    expiration,
    notes: notes ? 'optional' : 'hidden',
    paperExam: type === 'paper-exam',
    registration: registration
      ? 'required'
      : type === 'instructor-insurance-waiver' || type === 'first-aid-certificate'
        ? 'optional'
        : 'hidden',
  };
}

export function hasRequiredCustomerDocumentFormValues(
  type: CustomerDocumentCommandType,
  values: CustomerDocumentFormValues,
  today = calendarDateInTimeZone(new Date(), browserCalendarTimeZone())
) {
  return Object.keys(getCustomerDocumentFormErrors(type, values, today)).length === 0;
}

export type CustomerDocumentField = keyof CustomerDocumentFormValues | 'type' | 'status' | 'file';
export type CustomerDocumentFieldErrors = Partial<
  Record<CustomerDocumentField, 'required' | 'date' | 'examDateFuture' | 'empty' | 'size' | 'type'>
>;

/** Field feedback uses the same type matrix and date rules as command admission. */
export function getCustomerDocumentFormErrors(
  type: CustomerDocumentCommandType | '',
  values: CustomerDocumentFormValues,
  today = calendarDateInTimeZone(new Date(), browserCalendarTimeZone())
): CustomerDocumentFieldErrors {
  if (!type) return { type: 'required' };
  const rules = getCustomerDocumentFormRules(type);
  const errors: CustomerDocumentFieldErrors = {};
  if (rules.registration === 'required' && !hasValue(values.registrationId))
    errors.registrationId = 'required';
  if (rules.certificationType === 'required' && !hasValue(values.certificationTypeId))
    errors.certificationTypeId = 'required';
  if (rules.certifyingAgency === 'required' && !hasValue(values.certifyingAgency))
    errors.certifyingAgency = 'required';
  if (rules.expiration !== 'hidden') {
    if (!hasValue(values.expiresAt)) {
      if (rules.expiration === 'required') errors.expiresAt = 'required';
    } else if (!z.iso.date().safeParse(values.expiresAt).success) errors.expiresAt = 'date';
  }
  if (rules.paperExam) {
    if (!hasValue(values.paperExamDate)) errors.paperExamDate = 'required';
    else if (!z.iso.date().safeParse(values.paperExamDate).success) errors.paperExamDate = 'date';
    else if (values.paperExamDate > today) errors.paperExamDate = 'examDateFuture';
    for (const field of ['educationModuleId', 'examResultId', 'submittingInstructorId'] as const) {
      if (!hasValue(values[field])) errors[field] = 'required';
    }
  }
  return errors;
}

export function validateCustomerDocumentFile(file: File): 'empty' | 'size' | 'type' | null {
  if (file.size === 0) return 'empty';
  if (file.size > customerDocumentMaxFileBytes) return 'size';
  return resolveCustomerDocumentFileMimeType(file) === null ? 'type' : null;
}

export function resolveCustomerDocumentFileMimeType(
  file: File
): CustomerDocumentFileMimeType | null {
  const extension = file.name.trim().split('.').pop()?.toLowerCase();
  const mimeTypeByExtension =
    extension === undefined
      ? undefined
      : customerDocumentMimeTypeByExtension[
          extension as keyof typeof customerDocumentMimeTypeByExtension
        ];
  if (mimeTypeByExtension === undefined) return null;

  const browserMimeType = file.type.trim().toLowerCase();
  if (browserMimeType.length === 0) return null;
  const normalizedBrowserMimeType =
    browserMimeType === 'image/jpg' || browserMimeType === 'image/pjpeg'
      ? 'image/jpeg'
      : browserMimeType;
  if (!allowedFileTypes.has(normalizedBrowserMimeType as CustomerDocumentFileMimeType)) return null;

  return normalizedBrowserMimeType === mimeTypeByExtension ? mimeTypeByExtension : null;
}
